import type { Driver } from '@multirx/shared';
import { fetchDrivers, fetchDriverById, importDriversBackup, deleteDriver } from './api.js';
import {
  renderDriverList,
  setDriverListLoading,
  setDriverListError,
  mountDriverListToolbar,
  applyDriverListFilters,
  updateDriverListCount,
  syncDriverListSelection,
  DEFAULT_DRIVER_LIST_FILTERS,
  type DriverListFilters,
} from './driverListView.js';
import {
  renderDriverDetail,
  setDriverDetailLoading,
  setDriverDetailError,
} from './driverDetailView.js';
import { mountCreateDriverModal } from './createDriverModal.js';
import { createGarageEntityTitleBlock } from '../garage/garageEntityHeader.js';
import { createPillButton } from '../ui/pillButton.js';
import { wrapInPillButtonGroup } from '../ui/pillButtonGroup.js';
import { openConfirmModal } from '../ui/confirmModal.js';
import { exportDriversToFile, readDriverBackupFromFile } from '../garage/garageBackup.js';
import { applyRoleVisibility } from '../auth/navAccount.js';
import type { GaragePaneHandle, GaragePaneHosts } from '../garage/garageEmbed.js';
import { parseGarageNumericId } from '../garage/garageHash.js';

import { SELECTED_DRIVER_STORAGE_KEY as SELECTED_ID_KEY } from './openDriverDetail.js';

const FILTERS_KEY = 'multirx-driver-list-filters';

function loadSavedFilters(): DriverListFilters {
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    if (!raw) return { ...DEFAULT_DRIVER_LIST_FILTERS };
    const parsed = JSON.parse(raw) as DriverListFilters;
    return { ...DEFAULT_DRIVER_LIST_FILTERS, ...parsed };
  } catch {
    return { ...DEFAULT_DRIVER_LIST_FILTERS };
  }
}

function saveFilters(filters: DriverListFilters): void {
  sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
}

function appendDriverActions(
  actionsHost: HTMLElement,
  opts: {
    onNew: () => void;
    onImportClick: () => void;
    onExport: () => void;
  }
): void {
  const newDriverBtn = createPillButton('Nouveau', {
    variant: 'soft',
    ariaLabel: 'Nouveau pilote',
    onClick: opts.onNew,
  });
  newDriverBtn.dataset.role = 'admin';

  const importBtn = createPillButton('Importer', {
    variant: 'soft',
    onClick: opts.onImportClick,
  });
  importBtn.dataset.role = 'admin';

  const exportBtn = createPillButton('Exporter', {
    variant: 'soft',
    onClick: opts.onExport,
  });

  actionsHost.appendChild(
    wrapInPillButtonGroup([newDriverBtn, importBtn, exportBtn], {
      small: true,
      overflow: true,
      ariaLabel: 'Actions pilotes',
    })
  );
  applyRoleVisibility();
}

/** Montage embarqué dans le shell garage. */
export function mountDriversPane(hosts: GaragePaneHosts): GaragePaneHandle {
  const { listHost, detailHost, actionsHost, modalRoot, initialId, onSelectId } = hosts;

  listHost.innerHTML = '';
  detailHost.innerHTML = '';
  actionsHost.innerHTML = '';

  const listContainer = document.createElement('div');
  listContainer.className = 'circuits-sidebar garage-sidebar';

  const listToolbar = document.createElement('div');
  listToolbar.className = 'garage-sidebar-toolbar-host';

  const listInner = document.createElement('div');
  listInner.className = 'circuits-sidebar-inner garage-sidebar-list';

  listContainer.append(listToolbar, listInner);
  listHost.appendChild(listContainer);

  const detailContainer = detailHost;
  detailContainer.classList.add('circuits-main');

  const importInput = document.createElement('input');
  importInput.type = 'file';
  importInput.accept = 'application/json,.json';
  importInput.hidden = true;
  modalRoot.appendChild(importInput);

  let drivers: Driver[] = [];
  let filters: DriverListFilters = loadSavedFilters();
  const fromStore = parseInt(sessionStorage.getItem(SELECTED_ID_KEY) ?? '', 10);
  let selectedId: number | null =
    parseGarageNumericId(initialId ?? null) ??
    (Number.isFinite(fromStore) && fromStore > 0 ? fromStore : null);

  let openCreateDriver: () => void = () => undefined;

  appendDriverActions(actionsHost, {
    onNew: () => openCreateDriver(),
    onImportClick: () => importInput.click(),
    onExport: () => {
      void (async (): Promise<void> => {
        try {
          const all = await fetchDrivers(1000);
          if (all.length === 0) {
            window.alert('Aucun pilote à exporter.');
            return;
          }
          exportDriversToFile(all);
        } catch (err) {
          window.alert(err instanceof Error ? err.message : 'Export impossible.');
        }
      })();
    },
  });

  const toolbar = mountDriverListToolbar(listToolbar, filters, (next) => {
    filters = next;
    saveFilters(filters);
    refreshList();
  });

  function refreshList(): void {
    const filtered = applyDriverListFilters(drivers, filters);
    updateDriverListCount(listToolbar, filtered.length, drivers.length);
    renderDriverList(listInner, filtered, (id) => selectDriver(id), selectedId, {
      emptyFiltered: drivers.length > 0,
    });
  }

  const createModal = mountCreateDriverModal(modalRoot, (driver) => {
    drivers = [driver, ...drivers];
    refreshList();
    if (driver.id != null) selectDriver(driver.id);
  });
  openCreateDriver = (): void => createModal.open();

  function loadList(): Promise<void> {
    setDriverListLoading(listInner, true);
    return fetchDrivers(1000)
      .then((data) => {
        drivers = data;
        toolbar.setFilters(filters);
        refreshList();
        if (selectedId !== null) {
          const exists = drivers.find((d) => d.id === selectedId);
          if (exists) selectDriver(selectedId);
          else loadDetail(selectedId);
        } else {
          renderDriverDetail(detailContainer, null);
        }
      })
      .catch((err) => {
        setDriverListError(listInner, err instanceof Error ? err.message : 'Erreur inconnue');
        renderDriverDetail(detailContainer, null);
      });
  }

  function selectDriver(id: number): void {
    selectedId = id;
    sessionStorage.setItem(SELECTED_ID_KEY, String(id));
    syncDriverListSelection(listInner, selectedId);
    onSelectId?.(String(id));
    loadDetail(id);
  }

  function clearSelection(): void {
    selectedId = null;
    sessionStorage.removeItem(SELECTED_ID_KEY);
    syncDriverListSelection(listInner, null);
    onSelectId?.(null);
    renderDriverDetail(detailContainer, null);
  }

  function handleDeleteDriver(driver: Driver): void {
    if (driver.id == null) return;
    const name = `${driver.firstName} ${driver.lastName}`.trim();
    void openConfirmModal(modalRoot, {
      title: 'Supprimer le pilote',
      message: `Supprimer définitivement « ${name} » ?\n\nCette action est irréversible.`,
      confirmLabel: 'Supprimer',
      cancelLabel: 'Annuler',
      danger: true,
    }).then((confirmed) => {
      if (!confirmed) return;
      void deleteDriver(driver.id!)
        .then(() => {
          drivers = drivers.filter((d) => d.id !== driver.id);
          refreshList();
          if (selectedId === driver.id) clearSelection();
        })
        .catch((err) => {
          window.alert(err instanceof Error ? err.message : 'Erreur lors de la suppression');
        });
    });
  }

  function loadDetail(id: number): void {
    setDriverDetailLoading(detailContainer, true);
    fetchDriverById(id)
      .then((driver) => {
        renderDriverDetail(detailContainer, driver, {
          onSaved: (saved) => {
            const idx = drivers.findIndex((d) => d.id === saved.id);
            if (idx >= 0) drivers[idx] = saved;
            refreshList();
          },
          onDelete: handleDeleteDriver,
        });
      })
      .catch((err) => {
        setDriverDetailError(
          detailContainer,
          err instanceof Error ? err.message : 'Erreur inconnue'
        );
      });
  }

  importInput.addEventListener('change', () => {
    const file = importInput.files?.[0];
    importInput.value = '';
    if (!file) return;
    void (async (): Promise<void> => {
      try {
        const backup = await readDriverBackupFromFile(file);
        const result = await importDriversBackup(backup);
        await loadList();
        const first = result.drivers[0];
        if (first?.id != null) {
          selectDriver(first.id);
        }
        window.alert(`${result.imported} pilote(s) importé(s) avec succès.`);
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Import impossible.');
      }
    })();
  });

  void loadList();

  return {
    dispose() {
      listHost.innerHTML = '';
      detailHost.innerHTML = '';
      actionsHost.innerHTML = '';
      importInput.remove();
    },
  };
}

/** Montage page autonome (tests / compat). */
export function mountDriversApp(root: HTMLElement): void {
  const listHost = document.createElement('div');
  const detailHost = document.createElement('div');
  detailHost.className = 'circuits-main';

  const layout = document.createElement('div');
  layout.className = 'circuits-layout';
  layout.appendChild(listHost);
  layout.appendChild(detailHost);

  const header = document.createElement('header');
  header.className = 'circuits-header';
  header.style.display = 'flex';
  header.style.alignItems = 'center';
  header.style.justifyContent = 'space-between';
  header.style.flexWrap = 'wrap';
  header.style.gap = '1rem';
  const titleBlock = createGarageEntityTitleBlock('pilotes');
  const headerActions = document.createElement('div');
  headerActions.className = 'circuits-header-actions';
  headerActions.style.display = 'flex';
  headerActions.style.gap = '0.5rem';
  headerActions.style.flexWrap = 'wrap';
  headerActions.style.alignItems = 'center';

  header.appendChild(titleBlock);
  header.appendChild(headerActions);

  root.innerHTML = '';
  root.appendChild(header);
  root.appendChild(layout);

  mountDriversPane({
    listHost,
    detailHost,
    actionsHost: headerActions,
    modalRoot: root,
  });
}
